import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  type ReactNode,
  type Ref,
} from "react";
import {
  Send,
  Square,
  Loader2,
  Bot,
  Paperclip,
  Database,
  CornerUpLeft,
  X,
} from "lucide-react";
import { useReplyToNote, clearReplyToNote } from "@/components/ai/replyToNote";
import { buildNoteReplyContent } from "./noteReplyToken";
import { Button } from "@stockline/ui/components/button";
import { Textarea } from "@stockline/ui/components/textarea";
import { ScrollArea } from "@stockline/ui/components/scroll-area";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useChatSession, type ChatMode } from "./useChatSession";
import { RichContentRenderer, ChartSkeleton } from "@/components/charts";
import { deriveWidgetIntent, type WidgetIntent } from "./widgetIntent";
import { MessageBubble } from "./MessageBubble";
import { EmptyConversationSuggestions } from "./EmptyConversationSuggestions";
import { ProcessingBubble } from "./ProcessingBubble";
import { MediaPreviewCard, type PendingFile } from "./MediaPreviewCard";
import type {
  ChatMessage as V1ChatMessage,
  ChatLogEntry,
  ChatMediaFile,
  RichContent,
} from "@/app/api/types/chat-sessions";
import api from "@/app/api";
import { uploadChatMedia } from "@/app/ai/hooks/useChatMediaUpload";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ModifyBufferStoreContext,
  createModifyBufferStore,
} from "@/app/recap/stores/modifyBufferStore";
import { useAiAssistantName } from "@/query/hooks/useAiAssistantName";
import { useDragSelectAutoScroll } from "@/hooks/useDragSelectAutoScroll";
import {
  setActiveSessionId,
  clearActiveSessionId,
  setIsFocused,
  type ReplyToNote,
} from "@stockline/web";

export interface ChatWindowHandle {
  /**
   * Move keyboard focus to the message textarea. The parent owns the
   * decision to call this — typically in response to a user gesture
   * (e.g. picking a conversation in the sidebar). ChatWindow itself
   * never infers focus intent from prop changes.
   */
  focus: () => void;
  /**
   * Replace the composer draft with `text` and focus it. Used by callers
   * that offer one-tap starter prompts (e.g. BI launchpad suggestions).
   */
  prefill: (text: string) => void;
  /**
   * Send `text` immediately as a message — creating a session first if none
   * is selected — then clear the composer. Used by callers that hand off an
   * already-composed prompt (e.g. the home-page chat box navigating to `/ai`).
   * Empty/whitespace text is ignored.
   */
  sendNow: (text: string) => Promise<void>;
}

interface ChatWindowProps {
  sessionId: string | null;
  onSessionCreated: (id: string) => void;
  mode: ChatMode;
  /** Max width (px) of the centered messages/input column. */
  messageColumnMaxWidth?: number;
  /**
   * Opt-in starter prompts. When provided (and no `emptySuggestions` pills),
   * the empty-state composer shows an animated, cycling placeholder of these
   * prompts instead of the static one, and the verbose example list is hidden.
   */
  placeholderPrompts?: string[];
  /** Optional content rendered just above the composer (e.g. pinned-widget
   *  context pills in the BI chat). */
  aboveComposer?: ReactNode;
  /**
   * One-tap starter prompts (already translated) shown while the conversation
   * has no messages yet — tapping one **prefills** the composer so the user can
   * review/edit before sending (not auto-send). Used by the BI chat dock, where
   * the session is created lazily on first send, so these render in the
   * null-session `EmptyState` straight above the composer.
   */
  emptySuggestions?: string[];
  /** Heading above the empty-conversation suggestion pills. */
  emptySuggestionsHeading?: string;
  ref?: Ref<ChatWindowHandle>;
  /** Hide the file-attachment affordance (mobile/text-only composer). */
  hideAttachments?: boolean;
  /**
   * Fired once per user send, immediately after the message is dispatched
   * (session already bootstrapped, view-context snapshot already read). Lets a
   * caller consume any one-shot, per-message context it staged — the BI dock
   * uses it to clear pinned-widget attachments so a pin rides only the single
   * message it was attached to, not every subsequent turn.
   */
  onMessageSent?: () => void;
  /**
   * Fired once when a statistics-mode turn finishes (the processing false-edge).
   * The BI dock uses it to kick an immediate dashboard poll so an AI-authored
   * widget lands on the board with the answer, not up to a poll-interval later.
   */
  onTurnComplete?: () => void;
  /**
   * The widget the current statistics turn is building, published as it becomes
   * known and cleared (null) when the turn ends. The BI dashboard uses it to
   * render a ghost skeleton card on the board at the size the real widget will
   * take, so the answer and the widget don't visibly race. Fired only in
   * statistics mode; may be called with null early (intent not derivable yet).
   */
  onPendingWidget?: (intent: WidgetIntent | null) => void;
}

const DEFAULT_MESSAGE_COLUMN_MAX_WIDTH = 1024;

/**
 * File types accepted by the chat composer's attach-file input. Shared between
 * the pre-session `EmptyState` composer and the main `ChatWindow` composer so
 * the two never silently diverge on what they allow.
 */
const ACCEPTED_FILE_TYPES =
  "image/*,application/pdf,audio/*,video/*,.doc,.docx,.xls,.xlsx,.txt,.csv";

function ReplyToNoteChip({
  note,
}: Readonly<{
  note: ReplyToNote;
}>) {
  const { t } = useTranslation();

  return (
    <div
      className="mb-1.5 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300"
      data-testid="reply-to-note-chip"
    >
      <CornerUpLeft className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        {t("common.aiComments.replyingToNote")}
        {note.comment ? `: ${note.comment}` : ""}
      </span>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 hover:bg-violet-100 dark:hover:bg-violet-900/60"
        onClick={clearReplyToNote}
        title={t("common.aiComments.stopReplying")}
        aria-label={t("common.aiComments.stopReplying")}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// A same-turn mode switch persists a PARTIAL assistant row before it restarts
// under the new mode; that row's LAST tool call is switch_chat_mode (the call
// that ended the run). It is internal plumbing (its tools already surface in
// the restarted reply's aggregated footer and the live processing bubble), so
// it never renders its own bubble.
//
// But ending with switch_chat_mode does NOT make a row a partial: a REFUSED
// switch (target mode gated for this tenant, STK-1793) keeps the turn in its
// mode and completes normally — the REAL reply then also ends with the switch
// call, and hiding it would swallow the assistant's answer entirely. The
// discriminator is the restart itself: a partial always has a LATER assistant
// row from the SAME execution (the restarted run's reply); a refusal never
// does. A row that ends with the switch but has no such sibling must render.
const endsWithModeSwitch = (m: V1ChatMessage) =>
  m.tool_calls?.at(-1)?.name === "switch_chat_mode";

const isModeSwitchPartial = (
  m: V1ChatMessage,
  idx: number,
  messages: readonly V1ChatMessage[],
) =>
  endsWithModeSwitch(m) &&
  m.execution_id != null &&
  messages.some(
    (later, j) =>
      j > idx &&
      later.role === "assistant" &&
      later.execution_id === m.execution_id,
  );

// The live processing bubble stays up while the turn has no settled assistant
// reply yet — including the window where the restarted run is still working and
// the last row is the mid-turn switch partial (its restarted sibling hasn't
// landed yet, so the position-based check above can't identify it here).
function shouldShowProcessingBubble(
  messages: readonly V1ChatMessage[],
  isProcessing: boolean,
): boolean {
  if (!isProcessing) return false;
  const last = messages.at(-1);
  if (last?.role !== "assistant") return true;
  return endsWithModeSwitch(last);
}

export function ChatWindow({
  sessionId,
  onSessionCreated,
  mode,
  messageColumnMaxWidth = DEFAULT_MESSAGE_COLUMN_MAX_WIDTH,
  placeholderPrompts,
  aboveComposer,
  emptySuggestions,
  emptySuggestionsHeading,
  ref,
  hideAttachments = false,
  onMessageSent,
  onTurnComplete,
  onPendingWidget,
}: ChatWindowProps) {
  const { t } = useTranslation();
  const aiName = useAiAssistantName();
  // Active "replying to an AI note" context — drives the violet chip above the
  // composer and tags the outgoing message with a [Note reply <id>] token.
  const replyToNote = useReplyToNote();
  const [input, setInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [persistedRichContent, setPersistedRichContent] = useState<
    RichContent[]
  >([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastProcessingRef = useRef(false);
  // Coalesces concurrent first-upload session creation. `addFiles` fires one
  // `uploadFile` per selected file without awaiting, so a multi-file first
  // message would otherwise have each call see no session and create its own —
  // splitting the attachments across orphaned sessions. The first caller stores
  // its in-flight creation here; the rest await the same promise.
  const sessionCreationRef = useRef<Promise<string> | null>(null);
  // Pre-order proposals embedded in MessageBubble render PreOrderSection,
  // which needs the recap modify-buffer store. Outside recap we mount our
  // own store at the chat-window level so all proposal cards in this chat
  // share one buffer.
  const modifyBufferStoreRef = useRef(createModifyBufferStore());

  const isStatsMode = mode === "statistics";

  const {
    session,
    isLoading,
    isProcessing,
    isWaitingForInput,
    send,
    isSending,
    interrupt,
    isInterrupting,
    create,
    flushSessions,
    isCreating,
  } = useChatSession(sessionId, mode);

  // Publish "which chat is the user actively looking at" to the global
  // active-session store. Both the /ai page and the floating side panel render
  // this single ChatWindow, so this one writer covers every surface. The store
  // drives the unread flicker fix: the list + badge treat the active+focused
  // session as read at display time (no dot/badge flash on a reply landing in
  // the chat you're viewing), and `useClearUnreadOnFocus` reconciles the row.
  useEffect(() => {
    setActiveSessionId(sessionId);
    return () => {
      // Only clear if this session is still the active one — a fast switch may
      // have already set the next session before this cleanup runs.
      if (sessionId) clearActiveSessionId(sessionId);
    };
  }, [sessionId]);

  // Track tab visibility so a background tab doesn't count as "looking at" the
  // chat. Gate on visibility ALONE — not `document.hasFocus()`. A visible,
  // foreground tab that merely lacks OS keyboard focus (second monitor, split
  // screen, an embedded/automated webview) is still one the user can see and
  // read, so it must clear unread. Tying the reconciler to `hasFocus()` left
  // such tabs permanently unread for real users and made the unread badge
  // non-deterministic under headless automation (hasFocus() is frequently false
  // there). visibilitychange is the authoritative signal; focus/blur just prompt
  // a re-read of the visibility state.
  useEffect(() => {
    const sync = () => setIsFocused(document.visibilityState === "visible");
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    window.addEventListener("blur", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener("blur", sync);
    };
  }, []);

  // Whether the view should follow new content. Stays true while the user
  // is parked near the bottom; flips false the moment they scroll up to read
  // earlier messages, so tool-use/log updates don't yank them back down.
  const stickToBottomRef = useRef(true);

  const getViewport = useCallback(
    () =>
      scrollRef.current?.querySelector<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      ) ?? null,
    [],
  );

  const scrollToBottom = useCallback(() => {
    const viewport = getViewport();
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [getViewport]);

  // Let a text-selection drag past the top/bottom edge scroll the message
  // viewport (and keep extending the selection), instead of stopping dead at
  // whatever is currently on screen.
  useDragSelectAutoScroll(getViewport);

  // Track the user's scroll position. Treat anything within ~80px of the
  // bottom as "at bottom" so a slightly-off resting position still follows.
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport) return;
    const handleScroll = () => {
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      stickToBottomRef.current = distanceFromBottom <= 80;
    };
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
    // sessionId + isLoading re-run this once the scroll view actually mounts
    // (the ScrollArea is gated behind the loading/empty early returns, so the
    // viewport doesn't exist on the first pass for a session that loads).
  }, [getViewport, sessionId, isLoading]);

  // On session switch (and once it finishes loading), start pinned to bottom.
  useEffect(() => {
    stickToBottomRef.current = true;
    scrollToBottom();
  }, [sessionId, isLoading, scrollToBottom]);

  // Follow new content only while the user is parked at the bottom.
  useEffect(() => {
    if (stickToBottomRef.current) {
      scrollToBottom();
    }
  }, [
    session?.messages,
    session?.current_logs,
    persistedRichContent,
    scrollToBottom,
  ]);

  // Extract and persist rich content from logs (statistics mode)
  useEffect(() => {
    if (isStatsMode && session?.current_logs) {
      const richContents = session.current_logs
        .filter((log: ChatLogEntry) => log.rich_content)
        .map((log: ChatLogEntry) => log.rich_content as RichContent);

      if (richContents.length > 0) {
        setPersistedRichContent(richContents);
      }
    }
  }, [isStatsMode, session?.current_logs]);

  // Clear persisted content when starting a new query (statistics mode), and
  // signal turn completion on the processing false-edge. The BI dashboard polls
  // for AI-authored widgets on a 3s timer, so without a nudge the final answer
  // text (pushed live over SSE) can beat the widget onto the board by up to 3s —
  // the answer says "here's your chart" before the chart appears. `onTurnComplete`
  // lets the dashboard fire an immediate poll the moment the turn ends.
  useEffect(() => {
    if (isStatsMode) {
      if (isProcessing && !lastProcessingRef.current) {
        setPersistedRichContent([]);
      }
      if (!isProcessing && lastProcessingRef.current) {
        onTurnComplete?.();
      }
      lastProcessingRef.current = isProcessing;
    }
  }, [isStatsMode, isProcessing, onTurnComplete]);

  // The widget the current stats-mode turn is building, published to the board
  // so it can render a ghost skeleton card at the eventual size. Published from
  // a SEPARATE effect keyed on a PRIMITIVE (shape/title/size) so it fires ONLY
  // when the intent meaningfully changes — never on every render. (Publishing it
  // from the edge effect above looped: that effect depends on the unstable
  // `onTurnComplete`, so any parent re-render re-fired it, and the unconditional
  // setState-in-parent → "maximum update depth exceeded".) `onPendingWidget` is
  // a stable useCallback from usePendingWidget; the object is read from a ref so
  // the effect stays keyed on the primitive.
  const pendingWidgetIntent =
    isStatsMode && isProcessing
      ? deriveWidgetIntent(session?.current_logs)
      : null;
  // The board ghost is for NEW widgets only. `update_widget` edits an existing
  // card in place — the widget count never grows — so publishing its intent
  // would strand a phantom at the end of the grid for the whole grace window
  // (STK-1917 review). The in-chat skeleton still uses `pendingWidgetIntent`
  // to telegraph the shape; only the board publish drops updates.
  const boardWidgetIntent =
    pendingWidgetIntent && !pendingWidgetIntent.isUpdate
      ? pendingWidgetIntent
      : null;
  const pendingWidgetKey = boardWidgetIntent
    ? `${boardWidgetIntent.shape}|${boardWidgetIntent.title ?? ""}|${boardWidgetIntent.width ?? ""}|${boardWidgetIntent.height ?? ""}`
    : "";
  const boardWidgetIntentRef = useRef(boardWidgetIntent);
  boardWidgetIntentRef.current = boardWidgetIntent;
  useEffect(() => {
    onPendingWidget?.(boardWidgetIntentRef.current);
  }, [pendingWidgetKey, onPendingWidget]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
    };
  }, [pendingFiles]);

  // Auto-grow the composer with its content. The textarea ships `rows={1}` with
  // a CSS floor/cap (`min-h-[44px]`/`max-h-[200px]`) but `field-sizing` is fixed
  // under Tailwind 3, so without this it stays one row tall and long drafts hide
  // behind an internal scrollbar — which in the narrow BI dock reads as the box
  // never resizing. Reset to `auto` first so it shrinks back as text is deleted;
  // the CSS `max-h` caps growth and hands off to the scrollbar past 200px.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const getFileType = (file: File): ChatMediaFile["type"] => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type === "application/pdf") return "pdf";
    if (file.type.startsWith("audio/")) return "audio";
    if (file.type.startsWith("video/")) return "video";
    if (
      file.type.includes("document") ||
      file.type.includes("spreadsheet") ||
      file.type.includes("text/")
    )
      return "document";
    return "file";
  };

  // Reset per-session attachment state whenever the chat resets to a fresh
  // session (e.g. "New chat" sets sessionId back to null).
  //
  // 1. Drop the retained first-upload session promise. Otherwise the ref keeps
  //    the previous conversation's id, so the next attachment skips
  //    create()/onSessionCreated and uploads media into the old session while
  //    the prop stays null.
  // 2. Drop any still-pending attachments. They were uploaded against the now
  //    abandoned session's id, so carrying them into a fresh chat is wrong: with
  //    no session yet, handleSend's media branch calls send() with a null
  //    session, which silently no-ops while the composer is cleared — the
  //    message (and its files) just vanish with no feedback. Clearing here keeps
  //    the empty composer consistent: a fresh chat starts with no stale media.
  //
  // A non-null sessionId never consults the ref and never triggers this branch,
  // so clearing only on the null transition is enough and can't disturb an
  // in-flight first-upload (whose concurrent callers capture the promise
  // synchronously, and whose own create() flips sessionId truthy — never null —
  // before this effect can run). Reading pendingFiles via the functional updater
  // keeps the effect off the pendingFiles dependency, so attaching the first
  // file (which mutates pendingFiles while sessionId is still null) doesn't
  // re-run this and wipe the file the user just added.
  useEffect(() => {
    if (!sessionId) {
      sessionCreationRef.current = null;
      setPendingFiles((prev) => {
        if (prev.length === 0) return prev;
        prev.forEach((pf) => {
          if (pf.preview) URL.revokeObjectURL(pf.preview);
        });
        return [];
      });
    }
  }, [sessionId]);

  // Resolve the session to use for an upload or a send, coalescing concurrent
  // first-use bootstraps through `sessionCreationRef`. Prefer the live
  // `sessionId` prop; otherwise reuse the in-flight bootstrap promise (so a
  // multi-file first message — or an attach-then-send before the prop
  // propagates, or a text send after the attachment was removed — all land in
  // ONE session); otherwise create + flush a fresh session. On failure the ref
  // is cleared so a retry re-creates instead of awaiting a rejected promise.
  //
  // Both the media path (uploadChatMedia hits the server directly) and the text
  // path go through here, so the text-only send can never spawn a second
  // session while a first-upload bootstrap is still in flight.
  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    if (!sessionCreationRef.current) {
      sessionCreationRef.current = (async () => {
        const newSession = await create();
        onSessionCreated(newSession.id);
        // The media upload hits the server directly and validates the session
        // server-side. `create` only fire-and-forgets its push, so we must wait
        // for the session to reach the server first — otherwise the upload
        // races ahead and 404s with "Session not found".
        await flushSessions();
        return newSession.id;
      })().catch((err) => {
        // Drop the failed attempt so a retry re-creates the session instead of
        // awaiting a permanently-rejected promise.
        sessionCreationRef.current = null;
        throw err;
      });
    }
    return sessionCreationRef.current;
  }, [sessionId, create, onSessionCreated, flushSessions]);

  const uploadFile = useCallback(
    async (fileId: string, file: File) => {
      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === fileId ? { ...pf, uploadStatus: "uploading" as const } : pf,
        ),
      );

      try {
        // Ensure we have a session to upload to. Session creation/flush lives
        // inside the try so a failure here marks the file as errored (and
        // toasts) instead of leaving it stuck in "pending" with no feedback —
        // `addFiles` fires this without awaiting, so an escaping rejection
        // would otherwise be swallowed.
        const targetSessionId = await ensureSession();

        // Upload via the shared V2 chat-media uploader (returns id + url).
        const media = await uploadChatMedia(file, targetSessionId);
        setPendingFiles((prev) =>
          prev.map((pf) =>
            pf.id === fileId
              ? {
                  ...pf,
                  uploadStatus: "uploaded" as const,
                  uploadedUrl: media.url,
                  mediaId: media.id,
                }
              : pf,
          ),
        );
      } catch (err) {
        const raw = err instanceof Error ? err.message : "";
        const isStorageFull = raw.includes("Storage is full");
        const message = isStorageFull
          ? t("ai.storageFull")
          : t("ai.uploadFailed");
        toast.error(message);
        setPendingFiles((prev) =>
          prev.map((pf) =>
            pf.id === fileId
              ? {
                  ...pf,
                  uploadStatus: "error" as const,
                  error: message,
                }
              : pf,
          ),
        );
      }
    },
    [ensureSession, t],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const newPendingFiles: PendingFile[] = Array.from(files).map((file) => {
        const type = getFileType(file);
        return {
          id: crypto.randomUUID(),
          file,
          preview: type === "image" ? URL.createObjectURL(file) : undefined,
          type,
          uploadStatus: "pending" as const,
        };
      });
      setPendingFiles((prev) => [...prev, ...newPendingFiles]);

      // Upload each file immediately
      newPendingFiles.forEach((pf) => {
        uploadFile(pf.id, pf.file);
      });
    },
    [uploadFile],
  );

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => {
      const file = prev[index];
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files?.length) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        addFiles(e.target.files);
        e.target.value = ""; // Reset to allow selecting same file again
      }
    },
    [addFiles],
  );

  // Callback for interactive rich content (e.g. price proposal "Apply" button)
  const handleRichContentSend = useCallback(
    (message: string) => {
      // Explicit user action — re-pin so the reply is visible.
      stickToBottomRef.current = true;
      send({ content: message });
      // A rich-content reply is a real send too, so consume one-shot per-message
      // context (BI pinned widgets) here as well — otherwise a pin made while
      // answering an interactive card would leak onto every later turn.
      onMessageSent?.();
    },
    [send, onMessageSent],
  );

  const hasUploadError = pendingFiles.some((pf) => pf.uploadStatus === "error");
  const isUploadingAny = pendingFiles.some(
    (pf) => pf.uploadStatus === "uploading" || pf.uploadStatus === "pending",
  );

  // Text-only send: create a session on first message, then dispatch. Shared by
  // the composer's `handleSend` (text branch) and the imperative `sendNow`, so
  // the create-then-send path lives in exactly one place.
  const sendText = useCallback(
    async (content: string) => {
      if (!sessionId) {
        const newSession = await create();
        onSessionCreated(newSession.id);
        send({ content, targetSessionId: newSession.id });
      } else {
        send({ content });
      }
      // Same one-shot consume as `handleSend` — covers the `sendNow` handoff
      // path (e.g. BI starter prompts) so it, too, clears per-message context.
      onMessageSent?.();
    },
    [sessionId, create, onSessionCreated, send, onMessageSent],
  );

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      prefill: (text: string) => {
        setInput(text);
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      sendNow: async (text: string) => {
        const content = text.trim();
        if (!content) return;
        setInput(content);
        // Mirror handleSend: session bootstrap can fail, and sendNow is called
        // fire-and-forget (hand-off effect), so surface a toast and keep the
        // draft instead of leaking an unhandled rejection + silently clearing.
        try {
          await sendText(content);
          // Count handoff sends (home-page prompts, etc.) the same as composer
          // sends — fired only after the session-bootstrap-and-dispatch in
          // `sendText` resolves. Handoffs are always text-only, so no media.
          track("ai_chat_message_sent", { has_media: false });
          setInput("");
        } catch {
          toast.error(t("ai.sendFailed"));
        }
      },
    }),
    [sendText, t],
  );

  const handleSend = async () => {
    const content = input.trim();
    if (!content && pendingFiles.length === 0) return;

    // Mirror the Send button's guards: don't send (and clear the composer)
    // while attachments are still uploading or have errored, or the message
    // would post without their mediaIds.
    if (isUploadingAny || hasUploadError) return;

    // When replying to a note, prefix a [Note reply <id>] token so the chat
    // history shows it (parsed into a pill by MessageBubble). The note's full
    // content + ids ride the view-context snapshot separately for the LLM. The
    // chip stays up until the user removes it (sticky), so follow-ups keep the
    // note in context.
    const withReply = (text: string) =>
      replyToNote ? buildNoteReplyContent(replyToNote.id, text) : text;

    // Sending is an explicit user action — always re-pin so the user sees
    // their own message and the reply, even if they had scrolled up.
    stickToBottomRef.current = true;

    // Resolve (and, if needed, bootstrap) the session BEFORE touching the
    // composer. `ensureSession` prefers the live `sessionId` prop, else the
    // in-flight bootstrap promise (so a send issued before the prop propagated
    // — e.g. attach-then-send, or attach-remove-then-text-send — reuses the
    // SAME session instead of spawning a second, orphaned one), else creates
    // one. It can reject when create()/flushSessions() fails; `handleSend` is
    // fire-and-forget (Enter key, Send button), so an escaping rejection would
    // be a swallowed unhandled rejection that leaves the user with no feedback
    // and a half-cleared composer. Toast and bail with the draft intact so the
    // send can be retried.
    let targetSessionId: string;
    try {
      targetSessionId = await ensureSession();
    } catch {
      toast.error(t("ai.sendFailed"));
      return;
    }

    // AI chat usage signal — one event per sent message. Fired only AFTER the
    // guards above AND a successful session bootstrap, immediately before the
    // dispatch, so failed sessions never inflate the count. No message text;
    // `has_media` only flags whether attachments rode along.
    track("ai_chat_message_sent", { has_media: pendingFiles.length > 0 });

    if (pendingFiles.length > 0) {
      // Collect media IDs from already-uploaded files
      const mediaIds = pendingFiles
        .filter((pf) => pf.uploadStatus === "uploaded" && pf.mediaId)
        .map((pf) => pf.mediaId!);

      // Clean up previews
      pendingFiles.forEach((pf) => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
      setPendingFiles([]);

      send({
        content: withReply(content || t("common.sidePanelChat.attachedFiles")),
        mediaIds,
        targetSessionId,
      });
    } else {
      send({ content: withReply(content), targetSessionId });
    }

    // The dispatch above already read the view-context snapshot for THIS
    // message, so any one-shot per-message context (BI pinned widgets) is now
    // safe to consume — clearing it here keeps it off the next turn.
    onMessageSent?.();

    setInput("");
    textareaRef.current?.focus();
  };

  // Tapping an empty-conversation suggestion pill PREFILLS the composer (same as
  // the `prefill` imperative handle) rather than sending — the user reviews /
  // edits the prompt, then sends it themselves. The existing `input` effect
  // auto-grows the textarea to fit.
  const handleSuggestionPick = (text: string) => {
    setInput(text);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Empty state
  if (!sessionId && !isCreating) {
    return (
      <EmptyState
        isStatsMode={isStatsMode}
        aiName={aiName}
        input={input}
        setInput={setInput}
        textareaRef={textareaRef}
        handleKeyDown={handleKeyDown}
        handleSend={handleSend}
        isCreating={isCreating}
        placeholderPrompts={placeholderPrompts}
        emptySuggestions={emptySuggestions}
        emptySuggestionsHeading={emptySuggestionsHeading}
        onSuggestionPick={handleSuggestionPick}
        aboveComposer={aboveComposer}
        replyToNote={replyToNote}
        hideAttachments={hideAttachments}
        fileInputRef={fileInputRef}
        handleFileSelect={handleFileSelect}
        pendingFiles={pendingFiles}
        removeFile={removeFile}
        isUploadingAny={isUploadingAny}
        hasUploadError={hasUploadError}
        isDragOver={isDragOver}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
      />
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const messages = session?.messages ?? [];
  const showProcessingBubble = shouldShowProcessingBubble(
    messages,
    isProcessing,
  );

  return (
    <ModifyBufferStoreContext.Provider value={modifyBufferStoreRef.current}>
      <div className="flex flex-col h-full">
        {/* Messages Area */}
        <ScrollArea
          className="flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block"
          ref={scrollRef}
        >
          <div
            data-testid="chat-message-list"
            className="p-4 space-y-4 mx-auto w-full"
            style={{ maxWidth: `${messageColumnMaxWidth}px` }}
          >
            {messages.map((msg, idx) => {
              // Skip the mid-turn switch partial — it would draw a duplicate
              // tools footer off the shared execution beside the real reply.
              if (isModeSwitchPartial(msg, idx, messages)) return null;
              const isLastAssistant =
                msg.role === "assistant" &&
                idx ===
                  messages.reduce(
                    (last, m, i) => (m.role === "assistant" ? i : last),
                    -1,
                  );
              return (
                <MessageBubble
                  key={msg.id ?? msg.timestamp}
                  message={msg}
                  isStatsMode={isStatsMode}
                  isWaitingForInput={
                    isLastAssistant ? isWaitingForInput : false
                  }
                  onSendMessage={handleRichContentSend}
                />
              );
            })}

            {/* Live processing bubble — tool calls stack like ChatGPT/Claude.
                Hidden once the final assistant message has already been
                persisted (last message is assistant): the backend writes the
                message slightly before it flips the execution to COMPLETED, and
                in that window the streamed text in `current_logs` would
                duplicate the final MessageBubble. */}
            {showProcessingBubble && (
              <ProcessingBubble
                logs={session?.current_logs ?? []}
                isStatsMode={isStatsMode}
                onSendMessage={handleRichContentSend}
              />
            )}

            {/* Statistics mode: a chart-shaped skeleton telegraphs that the
                answer will be a graph, before the data lands. Once the agent
                has called its widget tool, tool_start already carries the real
                widget_type + title — so the skeleton takes the real shape and
                shows the real title, instead of the generic bar shimmer. */}
            {isStatsMode && isProcessing && (
              <div className="bg-card border rounded-lg p-4 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500">
                <ChartSkeleton
                  shape={pendingWidgetIntent?.shape}
                  label={
                    pendingWidgetIntent?.title ??
                    t("ai.buildingChart", "Building your chart…")
                  }
                />
              </div>
            )}

            {/* Persisted rich content (charts/tables) after processing - statistics mode */}
            {isStatsMode &&
              !isProcessing &&
              persistedRichContent.length > 0 && (
                <div className="space-y-4">
                  {persistedRichContent.map((rc, idx) => (
                    <div
                      key={idx}
                      className="bg-card border rounded-lg p-4 overflow-hidden"
                    >
                      <RichContentRenderer content={rc} />
                    </div>
                  ))}
                </div>
              )}

            {/* Error message */}
            {session?.error_message && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                {t("ai.executionFailed")}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div
          className="px-4 pb-4 pt-2 mx-auto w-full"
          style={{ maxWidth: `${messageColumnMaxWidth}px` }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {aboveComposer}

          {/* Empty-conversation starter pills (BI dock), sat right above the
              composer per the design. Tapping one prefills the composer; they
              fall away once the first message lands. */}
          {emptySuggestions &&
            emptySuggestions.length > 0 &&
            (session?.messages?.length ?? 0) === 0 &&
            !isProcessing && (
              <div className="mb-2">
                <EmptyConversationSuggestions
                  prompts={emptySuggestions}
                  heading={emptySuggestionsHeading}
                  onPick={handleSuggestionPick}
                />
              </div>
            )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept={ACCEPTED_FILE_TYPES}
          />

          {isDragOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-lg pointer-events-none z-10">
              <p className="text-primary font-medium">
                {t("common.sidePanelChat.dropFiles")}
              </p>
            </div>
          )}

          {/* Replying-to-note chip — note context rides this turn's
              view-context; this is the visible, removable affordance. */}
          {replyToNote && <ReplyToNoteChip note={replyToNote} />}

          <div
            className={cn(
              "relative rounded-2xl border bg-muted/40 transition-colors",
              isDragOver && "border-primary bg-primary/5",
              "focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-200/70",
            )}
          >
            {/* Pending files inside the input container */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3 pt-3">
                {pendingFiles.map((pf, idx) => (
                  <MediaPreviewCard
                    key={pf.id}
                    file={pf}
                    onRemove={() => removeFile(idx)}
                  />
                ))}
              </div>
            )}

            {/* Textarea */}
            <Textarea
              autoFocus
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              data-testid="chat-message-input"
              placeholder={getInputPlaceholder(
                t,
                isStatsMode,
                isProcessing,
                aiName,
              )}
              className="min-h-[44px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-4 py-3"
              rows={1}
              disabled={isStatsMode && isProcessing}
            />

            {/* Bottom toolbar */}
            <div className="flex items-center justify-between px-3 pb-2">
              <div className="flex items-center gap-1">
                {!isStatsMode && !hideAttachments && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                    onClick={() => fileInputRef.current?.click()}
                    title={t("common.sidePanelChat.attachFiles")}
                    data-attr="ai-chat-attach"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {isProcessing && input.trim() && (
                  <span className="text-xs text-muted-foreground">
                    {t("common.sidePanelChat.queued")}
                  </span>
                )}
                {isProcessing ? (
                  <Button
                    onClick={() => interrupt()}
                    disabled={isInterrupting}
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8 rounded-lg"
                    data-attr="ai-chat-stop"
                  >
                    {isInterrupting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={
                      (!input.trim() && pendingFiles.length === 0) ||
                      isSending ||
                      isUploadingAny ||
                      hasUploadError
                    }
                    size="icon"
                    className="h-8 w-8 rounded-lg"
                    data-attr="ai-chat-send"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModifyBufferStoreContext.Provider>
  );
}

function getInputPlaceholder(
  t: (key: string, opts?: Record<string, unknown>) => string,
  isStatsMode: boolean,
  isProcessing: boolean,
  aiName: string,
): string {
  if (isStatsMode) {
    return isProcessing
      ? t("common.sidePanelChat.waitForAnalysis")
      : t("common.sidePanelChat.askAboutData", { name: aiName });
  }
  return isProcessing
    ? t("common.sidePanelChat.addMessageOrSend")
    : t("common.sidePanelChat.typeMessage", { name: aiName });
}

/**
 * Cycling placeholder overlay for the empty-state composer. Rotates through
 * `prompts` with a fade/slide so the writing box itself "suggests" what to
 * ask. Non-interactive — sits behind the caret, lets clicks/typing through.
 */
function AnimatedPlaceholder({ prompts }: Readonly<{ prompts: string[] }>) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (prompts.length <= 1) return;
    const id = setInterval(
      () => setIndex((p) => (p + 1) % prompts.length),
      2800,
    );
    return () => clearInterval(id);
  }, [prompts.length]);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 overflow-hidden px-4 py-3"
      aria-hidden="true"
    >
      <span
        key={index}
        className="block truncate text-sm text-muted-foreground duration-500 animate-in fade-in slide-in-from-bottom-1"
      >
        {prompts[index]}
      </span>
    </div>
  );
}

function EmptyState({
  isStatsMode,
  aiName,
  input,
  setInput,
  textareaRef,
  handleKeyDown,
  handleSend,
  isCreating,
  placeholderPrompts,
  emptySuggestions,
  emptySuggestionsHeading,
  onSuggestionPick,
  aboveComposer,
  replyToNote,
  hideAttachments,
  fileInputRef,
  handleFileSelect,
  pendingFiles,
  removeFile,
  isUploadingAny,
  hasUploadError,
  isDragOver,
  handleDragOver,
  handleDragLeave,
  handleDrop,
}: {
  readonly isStatsMode: boolean;
  readonly aiName: string;
  readonly input: string;
  readonly setInput: (v: string) => void;
  readonly textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  readonly handleKeyDown: (e: React.KeyboardEvent) => void;
  readonly handleSend: () => void;
  readonly isCreating: boolean;
  readonly placeholderPrompts?: string[];
  readonly emptySuggestions?: string[];
  readonly emptySuggestionsHeading?: string;
  readonly onSuggestionPick: (prompt: string) => void;
  readonly aboveComposer?: ReactNode;
  readonly replyToNote: ReplyToNote | null;
  readonly hideAttachments: boolean;
  readonly fileInputRef: React.RefObject<HTMLInputElement | null>;
  readonly handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly pendingFiles: PendingFile[];
  readonly removeFile: (index: number) => void;
  readonly isUploadingAny: boolean;
  readonly hasUploadError: boolean;
  readonly isDragOver: boolean;
  readonly handleDragOver: (e: React.DragEvent) => void;
  readonly handleDragLeave: (e: React.DragEvent) => void;
  readonly handleDrop: (e: React.DragEvent) => void;
}) {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  // The BI launchpad supplies one-tap starter pills (`emptySuggestions`). In
  // that "lean" empty state we sit the pills straight above the composer and
  // drop the hero block, so the panel mirrors the in-conversation composer.
  const lean = isStatsMode && !!emptySuggestions?.length;
  // Legacy stats path (only `placeholderPrompts`, no pills): the writing box
  // cycles the prompts, so we keep the hero and skip the verbose description.
  const hasPrompts = isStatsMode && !!placeholderPrompts?.length && !lean;
  const EmptyIcon = isStatsMode ? Database : Bot;
  const title = isStatsMode
    ? t("common.sidePanelChat.databaseStatisticsTitle")
    : t("common.sidePanelChat.speakWith", { name: aiName });
  const description = isStatsMode
    ? t("common.sidePanelChat.emptyStateStatsDescription")
    : t("common.sidePanelChat.emptyStateUnifiedDescription", { name: aiName });
  const placeholder = isStatsMode
    ? t("common.sidePanelChat.askAboutData", { name: aiName })
    : t("common.sidePanelChat.typeMessageToStart", { name: aiName });
  return (
    <div
      className={cn(
        "flex flex-col h-full p-8 items-center",
        // Lean launchpad: pills + composer dock to the bottom like the real
        // in-conversation composer. Otherwise the hero block sits centered.
        lean ? "justify-end" : "justify-center text-center",
      )}
    >
      {!lean && (
        <>
          <EmptyIcon className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">{title}</h2>
          {!hasPrompts && (
            <p className="text-muted-foreground mb-6 max-w-md">{description}</p>
          )}
          {isStatsMode && !hasPrompts && (
            <div className="text-left text-sm text-muted-foreground mb-6 max-w-md">
              <p className="font-medium mb-2">
                {t("common.sidePanelChat.tryAsking")}
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t("common.sidePanelChat.exampleTopProducts")}</li>
                <li>{t("common.sidePanelChat.exampleOrderTrends")}</li>
                <li>{t("common.sidePanelChat.exampleFrequentCustomers")}</li>
                <li>{t("common.sidePanelChat.exampleComparePartners")}</li>
              </ul>
            </div>
          )}
        </>
      )}
      {/* Attachments are available before the first message too: the file
          input here shares ChatWindow's handlers, which auto-create the
          session on upload (see uploadFile). */}
      <div
        className="relative w-full max-w-lg"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {aboveComposer}
        {/* Friendly starter pills so the empty chat isn't a bare composer.
            Tapping one prefills the composer (onSuggestionPick). In the BI
            launchpad they stand in for the hero; in unified chat they sit above
            the composer the same way. */}
        {emptySuggestions && emptySuggestions.length > 0 && (
          <div className="mb-3 text-left">
            <EmptyConversationSuggestions
              prompts={emptySuggestions}
              heading={emptySuggestionsHeading}
              onPick={onSuggestionPick}
            />
          </div>
        )}
        {replyToNote && <ReplyToNoteChip note={replyToNote} />}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept={ACCEPTED_FILE_TYPES}
        />
        {isDragOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-2xl pointer-events-none z-10">
            <p className="text-primary font-medium">
              {t("common.sidePanelChat.dropFiles")}
            </p>
          </div>
        )}
        <div
          className={cn(
            "relative rounded-2xl border bg-muted/40 transition-colors focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-200/70",
            isDragOver && "border-primary bg-primary/5",
          )}
        >
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 px-3 pt-3">
              {pendingFiles.map((pf, idx) => (
                <MediaPreviewCard
                  key={pf.id}
                  file={pf}
                  onRemove={() => removeFile(idx)}
                />
              ))}
            </div>
          )}
          <Textarea
            autoFocus={!hasPrompts}
            ref={textareaRef}
            data-testid="chat-message-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={hasPrompts ? "" : placeholder}
            // When the visible placeholder is suppressed (so the animated
            // suggestions can take over), expose the same prompt to assistive
            // tech via aria-label — AnimatedPlaceholder itself is aria-hidden.
            aria-label={hasPrompts ? placeholder : undefined}
            className="min-h-[44px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-4 py-3"
            rows={1}
          />
          {hasPrompts && !input && !focused && placeholderPrompts && (
            <AnimatedPlaceholder prompts={placeholderPrompts} />
          )}
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center gap-1">
              {!isStatsMode && !hideAttachments && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  title={t("common.sidePanelChat.attachFiles")}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              onClick={handleSend}
              disabled={
                (!input.trim() && pendingFiles.length === 0) ||
                isCreating ||
                isUploadingAny ||
                hasUploadError
              }
              size="icon"
              className="h-8 w-8 rounded-lg"
              data-attr="ai-chat-send"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
